import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { t, NAV_LINKS, LOGO } from "../i18n/translations";

const Navbar = () => {
  const { lang, setLang } = useLang();
  const tr = t[lang];
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="navbar"
      className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-slate-100"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link">
            <div className="h-11 w-11 flex items-center justify-center">
              <img src={LOGO.light} alt="Revelo" className="h-11 w-11 object-contain" />
            </div>
            <span className="font-bold text-[#163B71] text-xl tracking-tight hidden sm:inline">
              Revelo
            </span>
          </Link>

          {/* Desktop nav - centered */}
          <nav className="hidden lg:flex flex-1 items-center justify-center gap-7" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.key}
                to={l.path}
                data-testid={`nav-link-${l.key}`}
                className={({ isActive }) =>
                  `nav-link-underline text-[15px] font-medium transition-colors ${isActive ? "text-[#163B71] active" : "text-slate-600 hover:text-[#163B71]"}`
                }
              >
                {tr.nav[l.key]}
              </NavLink>
            ))}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <div
              className="hidden sm:flex items-center rounded-full border border-slate-200 overflow-hidden text-sm"
              data-testid="language-switcher"
            >
              <button
                data-testid="lang-en-button"
                onClick={() => setLang("en")}
                className={`px-3 py-1.5 transition-colors ${
                  lang === "en"
                    ? "bg-[#163B71] text-white"
                    : "text-slate-600 hover:text-[#163B71]"
                }`}
              >
                EN
              </button>
              <button
                data-testid="lang-zh-button"
                onClick={() => setLang("zh")}
                className={`px-3 py-1.5 transition-colors ${
                  lang === "zh"
                    ? "bg-[#163B71] text-white"
                    : "text-slate-600 hover:text-[#163B71]"
                }`}
              >
                中文
              </button>
            </div>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((o) => !o)}
              className="lg:hidden p-2 rounded-md text-slate-700"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.key}
                to={l.path}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.key}`}
                className={({ isActive }) =>
                  `py-2.5 px-2 rounded-lg text-base font-medium ${
                    isActive ? "text-[#163B71] bg-slate-50" : "text-slate-700"
                  }`
                }
              >
                {t[lang].nav[l.key]}
              </NavLink>
            ))}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
              <div className="flex items-center rounded-full border border-slate-200 overflow-hidden text-sm">
                <button
                  onClick={() => setLang("en")}
                  data-testid="mobile-lang-en"
                  className={`px-3 py-1.5 ${lang === "en" ? "bg-[#163B71] text-white" : "text-slate-600"}`}
                >EN</button>
                <button
                  onClick={() => setLang("zh")}
                  data-testid="mobile-lang-zh"
                  className={`px-3 py-1.5 ${lang === "zh" ? "bg-[#163B71] text-white" : "text-slate-600"}`}
                >中文</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
